// 🗂 PJT 순수 로직 (2026-09-06 PJT 1차 A — 잔디 PMS 명세 "업무 관리 기본기") — mongo 의존 없음, pjtPure.test.ts 로 회귀.
//   routes/projects.ts 는 mongo·RC 호출이 뒤엉켜 있어 테스트가 어렵다 → 판정·정규화·diff 만 여기로 뺀다.
//   ① normalizeWatchers : 참조자를 프로젝트 인원(역할 무관) 중에서만 · owners 와 중복 제거 · ≤20
//   ② normalizeChecklist: 체크리스트 배열 전체 교체 — 항목 ≤50 · text ≤200 · id 없으면 서버가 부여
//   ③ diffCardChanges   : 카드 update/move 의 audit detail.changes (기록 탭에서 '이전값 › 새값' 으로 보여 준다)
//   ④ isClosed          : 프로젝트 종료(읽기 전용) 판정 — 쓰기 라우트 423 게이트의 근거
//   ⑤ relationOf        : 카드와 나의 관계(담당/참조/둘 다) — GET /my-cards 의 myRelation (2026-09-06 PJT 2차-A)
//   ⑥ kstWeekRange      : 이번 주(KST 일~토) 범위 — 홈 '이번 주 나의 업무' 7일 칩 (2026-09-06 PJT 2차-A)
//      kstWeekLabel      : 반복형 주차 단계 이름 'N주차(M/D~M/D)' — 서버 잡·클라 공용 (2026-10-05 토요일 +1 버그)
//   ⑦ suggestProjectKey : 업무 키(MT) 자동 제안 — 영문/숫자 → 한글 초성 로마자 → PJ, 충돌 시 숫자 접미 (2026-09-06 PJT 2차-B)
//   ⑧ assignCardNumbers : 카드 번호(no) 1..N 부여 — 1회 마이그레이션 잡이 쓴다 (2026-09-06 PJT 2차-B)
//   ⑨ visibleOnCalendar : 프로젝트 calendarScope(all/involved) 별 캘린더 노출 판정 (2026-09-06 PJT 2차-B)
//   ⑩ cardDateRange     : 카드가 캘린더에서 차지하는 KST 일자 구간 (2026-09-06 PJT 2차-B)
//   ⑪ aggregateAdminOverview : 관리자 전사 프로젝트 현황 집계 — KPI·프로젝트별·멤버별·지연 (2026-09-06 PJT 2차-C)

export type PjPerson = { userId: string; username: string; name: string };
export type PjChecklistItem = { id: string; text: string; done: boolean; order: number };

/** 카드 우선순위 — 없으면 normal (계약 §1). */
export const PJ_PRIORITIES: ReadonlySet<string> = new Set(['urgent', 'high', 'normal', 'low']);
export const PRIORITY_LABEL: Readonly<Record<string, string>> = { urgent: '긴급', high: '높음', normal: '보통', low: '낮음' };
export const CARD_STATUS_LABEL: Readonly<Record<string, string>> = { todo: '예정', doing: '진행', done: '완료' };

const s = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

/** 날짜 → YYYY-MM-DD (KST). 서버 TZ(UTC) 무관하게 +9h 로 계산 (datetime KST 규약). 빈 값·잘못된 값은 ''. */
export function kstDateOnly(v: unknown): string {
	if (v === null || v === undefined || v === '') return '';
	const d = v instanceof Date ? v : new Date(String(v));
	const t = d.getTime();
	if (!Number.isFinite(t)) return '';
	return new Date(t + 9 * 3600_000).toISOString().slice(0, 10);
}

/**
 * 참조자 정규화 — 입력 순서를 유지하되
 *   · 프로젝트 인원 목록(memberRows, watcher 역할 포함)에 없는 사람은 버린다(남의 계정 끼워넣기 차단)
 *   · owners 에 있는 사람은 제거(담당자는 토픽 알림으로 이미 받는다 — 계약 §1)
 *   · 같은 userId 중복 제거 · 최대 20명
 * 이름·username 은 저장된 인원 정보를 정본으로 쓴다(클라 입력값을 믿지 않는다 — ID 노출·위조 방지).
 */
export function normalizeWatchers(
	input: unknown,
	memberRows: ReadonlyArray<{ userId?: string; username?: string; name?: string }>,
	owners: ReadonlyArray<{ userId?: string }>,
): PjPerson[] {
	const arr = Array.isArray(input) ? input as Array<{ userId?: string } | null> : [];
	const ownerIds = new Set((owners || []).map((o) => s(o?.userId)).filter(Boolean));
	const byId = new Map<string, { username: string; name: string }>();
	for (const m of memberRows || []) {
		const id = s(m?.userId);
		if (id && !byId.has(id)) byId.set(id, { username: s(m?.username), name: s(m?.name) });
	}
	const out: PjPerson[] = [];
	const seen = new Set<string>();
	for (const w of arr) {
		const id = s(w?.userId);
		if (!id || seen.has(id) || ownerIds.has(id)) continue;
		const row = byId.get(id);
		if (!row) continue;
		seen.add(id);
		out.push({ userId: id, username: row.username, name: row.name });
		if (out.length >= 20) break;
	}
	return out;
}

/** 체크리스트 정규화 — update 는 배열 전체 교체(계약 §1). 빈 텍스트 항목은 버리고 order 는 0..n-1 로 다시 매긴다. */
export function normalizeChecklist(input: unknown, genId: () => string): PjChecklistItem[] {
	const arr = Array.isArray(input) ? input as Array<{ id?: unknown; text?: unknown; done?: unknown } | null> : [];
	const out: PjChecklistItem[] = [];
	const seen = new Set<string>();
	for (const it of arr) {
		const text = s(it?.text).trim().slice(0, 200);
		if (!text) continue;
		let id = s(it?.id).trim().slice(0, 40);
		if (!id || seen.has(id)) id = genId();
		seen.add(id);
		out.push({ id, text, done: it?.done === true, order: out.length });
		if (out.length >= 50) break;
	}
	return out;
}

export type PjChangeField = 'title' | 'status' | 'priority' | 'startAt' | 'dueAt' | 'owners' | 'watchers' | 'checklist' | 'sid';
export type PjChange = { field: PjChangeField; from: string; to: string };

const CHANGE_FIELDS: readonly PjChangeField[] = ['title', 'status', 'priority', 'startAt', 'dueAt', 'owners', 'watchers', 'checklist', 'sid'];

/** 사람 배열 → 이름 ' · ' join. 이름이 없으면 username 폴백(계정 ID 노출은 클라 표시에서 이미 걸러진다). */
function peopleLabel(v: unknown): string {
	if (!Array.isArray(v)) return '';
	return (v as Array<{ name?: unknown; username?: unknown } | null>)
		.map((p) => s(p?.name) || s(p?.username))
		.filter(Boolean)
		.join(' · ');
}

/** 사람 배열 → **userId 집합** 키(정렬·중복 제거). 같고 다름은 이 키로 본다 — 순서만 바뀐 것은 변경이 아니다 (2026-09-06 리뷰).
 *  userId 가 하나도 없는 구 데이터(이름만 저장)는 키를 만들 수 없으므로 이름 라벨로 폴백한다. */
function peopleKey(v: unknown): string {
	if (!Array.isArray(v)) return '';
	const arr = v as Array<{ userId?: unknown } | null>;
	const ids = [...new Set(arr.map((p) => s(p?.userId)).filter(Boolean))].sort();
	return ids.length ? ids.join(',') : peopleLabel(v);
}

/** 체크리스트 → 'done/total'. 배열이 아니면 '0/0'. */
function checklistLabel(v: unknown): string {
	if (!Array.isArray(v)) return '0/0';
	const arr = v as Array<{ done?: unknown } | null>;
	return `${arr.filter((c) => c?.done === true).length}/${arr.length}`;
}

/**
 * 카드 변경 diff — audit detail.changes. `after` 에 **키가 있는 필드만** 본다(= 이번 요청이 실제로 건드린 것).
 *   · 사람(owners/watchers)은 이름 ' · ' join · 날짜(startAt/dueAt)는 YYYY-MM-DD(KST) · checklist 는 done/total
 *   · sid 는 ctx.stageNames 로 단계명 치환(없으면 id 그대로 — 지워진 단계 대비)
 *   · priority 는 미지정=normal 로 보정 → '보통 › 보통' 같은 헛 변경을 만들지 않는다
 * 값이 같으면 항목을 넣지 않는다. 없는 값은 '' (클라가 '—' 등으로 표시).
 */
export function diffCardChanges(
	before: Record<string, unknown> | null | undefined,
	after: Record<string, unknown> | null | undefined,
	ctx?: { stageNames?: Map<string, string> | Record<string, string> },
): PjChange[] {
	const b = before || {};
	const a = after || {};
	const stageName = (v: unknown): string => {
		const id = s(v);
		if (!id) return '';
		const m = ctx?.stageNames;
		if (m instanceof Map) return m.get(id) || id;
		if (m && typeof m === 'object') return (m as Record<string, string>)[id] || id;
		return id;
	};
	const label = (f: PjChangeField, src: Record<string, unknown>): string => {
		switch (f) {
			case 'title': return s(src.title);
			case 'status': return CARD_STATUS_LABEL[s(src.status)] || s(src.status);
			case 'priority': { const p = s(src.priority) || 'normal'; return PRIORITY_LABEL[p] || p; }
			case 'startAt': return kstDateOnly(src.startAt);
			case 'dueAt': return kstDateOnly(src.dueAt);
			case 'owners': return peopleLabel(src.owners);
			case 'watchers': return peopleLabel(src.watchers);
			case 'checklist': return checklistLabel(src.checklist);
			case 'sid': return stageName(src.sid);
			default: return '';
		}
	};
	const out: PjChange[] = [];
	for (const f of CHANGE_FIELDS) {
		if (!(f in a)) continue;
		const from = label(f, b);
		const to = label(f, a);
		// 👥 사람 필드는 userId 집합으로 비교 (2026-09-06 리뷰) — 순서만 바뀌면 변경 아님 / 동명이인 교체는 라벨이 같아도 변경
		if (f === 'owners' || f === 'watchers') {
			if (peopleKey(b[f]) !== peopleKey(a[f])) out.push({ field: f, from, to });
			continue;
		}
		if (from !== to) out.push({ field: f, from, to });
	}
	return out;
}

export type PjRelation = 'owner' | 'watcher' | 'both' | 'none';

/**
 * 카드와 나의 관계 (2026-09-06 PJT 2차-A, 계약 §1-1) — GET /my-cards 의 `myRelation`.
 *   담당 = owners[].userId 또는 레거시 ownerId · 참조 = watchers[].userId · 둘 다면 'both'.
 * 'none' 은 방어값 — /my-cards 는 셋 중 하나로 이미 걸러서 조회하므로 실제로는 나오지 않는다.
 */
export function relationOf(card: { ownerId?: unknown; owners?: unknown; watchers?: unknown } | null | undefined, uid: string): PjRelation {
	const id = s(uid);
	if (!id) return 'none';
	const has = (v: unknown): boolean => Array.isArray(v) && (v as Array<{ userId?: unknown } | null>).some((p) => s(p?.userId) === id);
	const owner = s(card?.ownerId) === id || has(card?.owners);
	const watcher = has(card?.watchers);
	if (owner && watcher) return 'both';
	if (owner) return 'owner';
	if (watcher) return 'watcher';
	return 'none';
}

export type PjWeekRange = { start: Date; end: Date; startYmd: string; dayYmds: string[] };

/**
 * 이번 주(KST) 범위 (2026-09-06 PJT 2차-A, 계약 §1-2) — 주 시작은 **일요일**
 * (일정 모듈 components/calendar/model.ts startOfWeek 가 일요일 시작 · 잔디도 일요일).
 *   start = 그 주 일요일 00:00 KST(= UTC 로는 전날 15:00) · end = 다음 주 일요일 00:00 KST → 반열림 [start, end)
 *   dayYmds = 일~토 7개의 'YYYY-MM-DD'(KST) — 카드 분류는 이 문자열과 kstDateOnly(dueAt) 비교로 한다(시각 무관).
 * 서버 TZ(UTC) 와 무관하게 +9h 산술로만 계산한다(datetime KST 규약).
 */
export function kstWeekRange(now: number | Date): PjWeekRange {
	const raw = now instanceof Date ? now.getTime() : Number(now);
	const t = Number.isFinite(raw) ? raw : Date.now();   // 잘못된 입력에 1970년 주가 나오지 않게
	const DAY = 86400_000;
	const kstDay = Math.floor((t + 9 * 3600_000) / DAY);   // KST 기준 epoch 일수
	const dow = (kstDay + 4) % 7;                          // 1970-01-01 = 목요일 → +4 로 0=일요일
	const startDay = kstDay - dow;
	const dayYmds = Array.from({ length: 7 }, (_, i) => new Date((startDay + i) * DAY).toISOString().slice(0, 10));
	return { start: new Date(startDay * DAY - 9 * 3600_000), end: new Date((startDay + 7) * DAY - 9 * 3600_000), startYmd: dayYmds[0], dayYmds };
}

/**
 * 반복형 주차 단계 이름 'N주차(M/D~M/D)' — KST 일~토 (2026-10-05 원격 "최신 주차 생성 오류").
 *   🔴 구식 ceil((경과일수 + 1월1일요일 + 1)/7) 은 경과일수에 시각 소수점이 섞여 **토요일 00시 이후 번호가 +1** 로 넘어갔다
 *     → 10/3(토) 잡이 '41주차(9/27~10/3)' 를 만들고 10/4(일) 에 '41주차(10/4~10/10)' 가 또 생겨 41주차가 둘.
 *   번호는 그 주 **일요일 날짜**(정수 일수)로만 센다. 1주차 = 1월 1일이 든 주, 연도는 그 주 토요일 기준(연말 주가 둘로 갈리지 않게).
 *   일~금 번호는 구식과 같다 — 기존 단계 이름은 그대로 유효.
 */
export function kstWeekLabel(now: number | Date): string {
	const DAY = 86400_000;
	const startDay = Math.round((kstWeekRange(now).start.getTime() + 9 * 3600_000) / DAY);   // 일요일(KST) epoch 일수
	const sun = new Date(startDay * DAY);
	const sat = new Date((startDay + 6) * DAY);
	const jan1Day = Date.UTC(sat.getUTCFullYear(), 0, 1) / DAY;
	const jan1Dow = (jan1Day + 4) % 7;                                                    // 1970-01-01 = 목요일
	const week = Math.floor((startDay - jan1Day + jan1Dow) / 7) + 1;
	const f = (x: Date) => `${x.getUTCMonth() + 1}/${x.getUTCDate()}`;
	return `${week}주차(${f(sun)}~${f(sat)})`;
}

/** 프로젝트 종료(읽기 전용) 여부 — closedAt 이 유효한 값이면 종료. 쓰기 라우트는 423 (계약 §3-4). */
export function isClosed(project: { closedAt?: unknown } | null | undefined): boolean {
	const v = project?.closedAt;
	if (v === null || v === undefined || v === '' || v === false) return false;
	if (v instanceof Date) return Number.isFinite(v.getTime());
	return true;
}

// ── ④ 업무 키 `MT-12` (2026-09-06 PJT 2차-B, 계약 §2-1) ────────────────────────────────
//   프로젝트 키(2~4자) + 카드 번호(no) 로 사람이 부르는 주소를 만든다 — 채팅 자동 링크가 이 형식을 찾는다.

/** 키 형식 — 2~20자 [A-Z0-9] (2026-10-02 4→20자, ERP 프로젝트 번호 수용). 자동 제안은 여전히 짧게(2~4자). 클라 입력칸 유효성(계약 §2-2)과 같은 정규식. */
export const PJ_KEY_RE = /^[A-Z0-9]{2,20}$/;

/**
 * 🏷 프로젝트 태그 (2026-10-02 형 "Blog 처럼 key 밑에 # Tag — 최대 5개, PJT 메인에서 검색").
 *   배열 또는 쉼표·공백·# 로 구분한 문자열 → 앞 '#' 제거·공백 제거·20자·대소문자 무시 중복 제거·최대 5개. 클라 components/projects/pjTags.ts 와 같은 규칙.
 */
export const PJ_TAG_MAX = 5;
export function normalizeProjectTags(v: unknown): string[] {
	const raw: unknown[] = Array.isArray(v) ? v : typeof v === 'string' ? v.split(/[,\s#]+/) : [];
	const out: string[] = [];
	const seen = new Set<string>();
	for (const x of raw) {
		const tg = String(x ?? '').replace(/^#+/, '').replace(/\s+/g, '').slice(0, 20);
		const k = tg.toLowerCase();
		if (!tg || seen.has(k)) continue;
		seen.add(k);
		out.push(tg);
		if (out.length >= PJ_TAG_MAX) break;
	}
	return out;
}

/** 입력 키 정규화 — 공백 제거 + 대문자. 유일성 검증은 이 정규화 결과로 한다(계약 §2-1 "대문자 정규화"). */
export function normalizeProjectKey(v: unknown): string {
	return (v === null || v === undefined ? '' : String(v)).replace(/\s+/g, '').toUpperCase().slice(0, 20);
}

/**
 * 키 유효성 — 형식 + **영문 1자 이상**.
 * 🔴 전부 숫자인 키(`2026`)를 허용하면 채팅 자동 링크(`\b[A-Z0-9]{2,20}-\d{1,5}\b`)가 '2026-09' 같은 날짜·품번을
 *    카드 링크로 바꾼다 — 대화 본문 오염이라 되돌릴 방법이 없어 여기서 막는다 (2026-09-06 서버 판단, 계약 보고).
 */
export function isValidProjectKey(k: string): boolean {
	return PJ_KEY_RE.test(k) && /[A-Z]/.test(k);
}

// 한글 초성 → 로마자 (계약 §2-1 표) — 쌍자음은 기본 자음으로 접는다(ㄲ→g ㄸ→d ㅃ→b ㅆ→s ㅉ→j).
const CHOSUNG_ROMAN = ['g', 'g', 'n', 'd', 'd', 'r', 'm', 'b', 'b', 's', 's', 'o', 'j', 'j', 'c', 'k', 't', 'p', 'h'];

/** 이름 → 한글 음절 초성 로마자(대문자). '주간업무' → 'JGOM'. 한글이 없으면 ''. */
function chosungRoman(name: string): string {
	let out = '';
	for (const ch of name) {
		const c = ch.charCodeAt(0) - 0xAC00;
		if (c >= 0 && c < 11172) out += CHOSUNG_ROMAN[Math.floor(c / 588)];
	}
	return out.toUpperCase();
}

/** 후보 문자열에서 키 밑동을 뽑는다 — 앞의 숫자는 버리고(전부 숫자 키 방지), 2자 미만이거나 영문이 없으면 ''. */
function keyBase(chars: string, max: number): string {
	const s = chars.replace(/^[0-9]+/, '');
	return s.length >= 2 && /[A-Z]/.test(s) ? s.slice(0, max) : '';
}

/**
 * 키 자동 제안 (계약 §2-1) — 이름의 **영문 대문자/숫자 첫 2~4자** → 없으면 **한글 초성 로마자 첫 2~3자**
 * → 그래도 없으면 `PJ`, 충돌하면 숫자 접미(`JG` 사용 중 → `JG2`). 항상 isValidProjectKey 를 만족한다.
 *   · 대소문자 섞인 이름(`Monthly Task` → `MT`)은 대문자만 골라 약어가 되고, 전부 소문자면(`tptalk` → `TPTA`)
 *     영숫자 전체를 대문자로 올려 쓴다 — 대문자가 하나도 없다고 `PJ` 로 떨어지는 건 쓸모가 없다.
 *   · taken 은 **활성 프로젝트**의 키 집합(대문자 정규화). 보관 프로젝트는 유일성 대상이 아니다.
 */
export function suggestProjectKey(name: unknown, taken?: Iterable<string> | null): string {
	const nm = (name === null || name === undefined ? '' : String(name)).trim();
	const used = new Set<string>();
	for (const t of taken || []) { const k = normalizeProjectKey(t); if (k) used.add(k); }
	const base =
		keyBase((nm.match(/[A-Z0-9]/g) || []).join(''), 4)               // ① 영문 대문자·숫자
		|| keyBase((nm.match(/[A-Za-z0-9]/g) || []).join('').toUpperCase(), 4)   // ①' 전부 소문자인 이름
		|| keyBase(chosungRoman(nm), 3)                                   // ② 한글 초성 로마자
		|| 'PJ';                                                          // ③ 최후
	if (!used.has(base)) return base;
	for (let n = 2; n <= 999; n++) {
		const suf = String(n);
		const cand = base.slice(0, Math.max(1, 4 - suf.length)) + suf;
		if (isValidProjectKey(cand) && !used.has(cand)) return cand;
	}
	return base;   // 여기까지 오면 998개가 다 찬 것 — 유일성 검증(409)이 최종 방어
}

export type PjCardNoRow = { _id: string; createdAt?: unknown; no?: unknown };

/**
 * 카드 번호 부여 (계약 §2-1 마이그레이션) — **createdAt 오름차순**으로 1..N.
 *   · 이미 번호가 있는 카드는 그대로 두고 그 다음 번호부터 이어 붙인다 → 잡을 두 번 돌려도 번호가 뒤집히지 않는다(멱등).
 *   · seq 는 프로젝트 cardSeq 로 저장할 값(= 마지막 번호). 카드가 없으면 0.
 * 반환 updates 는 **실제로 바뀌는 카드만** — 호출부는 이것만 bulkWrite 한다.
 */
export function assignCardNumbers(cards: ReadonlyArray<PjCardNoRow> | null | undefined): { updates: Array<{ _id: string; no: number }>; seq: number } {
	const rows = [...(cards || [])];
	const ts = (v: unknown): number => { const d = v instanceof Date ? v : new Date(String(v ?? '')); const t = d.getTime(); return Number.isFinite(t) ? t : 0; };
	rows.sort((a, b) => ts(a?.createdAt) - ts(b?.createdAt) || String(a?._id).localeCompare(String(b?._id)));
	const num = (v: unknown): number => { const n = Number(v); return Number.isInteger(n) && n > 0 ? n : 0; };
	let seq = 0;
	for (const r of rows) seq = Math.max(seq, num(r?.no));
	const updates: Array<{ _id: string; no: number }> = [];
	for (const r of rows) {
		if (num(r?.no)) continue;
		updates.push({ _id: String(r._id), no: ++seq });
	}
	return { updates, seq };
}

// ── ② 카드 → 일정 캘린더 (2026-09-06 PJT 2차-B, 계약 §1-1) ────────────────────────────

export type PjCalendarScope = 'all' | 'involved';

/**
 * 캘린더 노출 판정 (계약 §1-1) — 프로젝트 `calendarScope` 가
 *   · `'all'`(기본)  → 그 프로젝트 **멤버 전원**이 전체 카드를 본다
 *   · `'involved'` → 담당(owners)·참조(watchers)·PM 만 본다
 * 보관된 카드·보관된 프로젝트는 언제나 제외(종료 프로젝트는 포함 — 읽기는 되는 상태다).
 * role 이 null 이면 그 프로젝트 멤버가 아니다 → 관리자라도 안 보인다(본인 멤버십 기준).
 */
export function visibleOnCalendar(
	card: { archivedAt?: unknown; ownerId?: unknown; owners?: unknown; watchers?: unknown } | null | undefined,
	project: { archivedAt?: unknown; calendarScope?: unknown; pmId?: unknown } | null | undefined,
	uid: string,
	role: 'pm' | 'member' | 'watcher' | null | undefined,
): boolean {
	if (!card || !project || !uid || !role) return false;
	if (card.archivedAt !== null && card.archivedAt !== undefined && card.archivedAt !== '') return false;
	if (project.archivedAt !== null && project.archivedAt !== undefined && project.archivedAt !== '') return false;
	if (project.calendarScope !== 'involved') return true;
	if (role === 'pm' || String(project.pmId || '') === uid) return true;
	return relationOf(card as { ownerId?: unknown; owners?: unknown; watchers?: unknown }, uid) !== 'none';
}

/**
 * 카드가 캘린더에서 차지하는 날짜 구간 (계약 §1-1) — `startAt || dueAt` ~ `dueAt` 의 KST 일자 문자열.
 * dueAt 이 없으면 그릴 수 없으므로 null(호출부가 제외). 잘못 저장된 startAt > dueAt 은 dueAt 으로 눌러 하루짜리로 만든다.
 */
export function cardDateRange(card: { startAt?: unknown; dueAt?: unknown } | null | undefined): { start: string; end: string } | null {
	const end = kstDateOnly(card?.dueAt);
	if (!end) return null;
	const s = kstDateOnly(card?.startAt);
	return { start: s && s < end ? s : end, end };
}

// ── ⑪ 관리자 전사 대시보드 집계 (2026-09-06 PJT 2차-C, 계약 §1-2) ────────────────────────
//   관리자 [프로젝트] 탭의 KPI 4 · 완료율 도넛 · 프로젝트별 진행률 · 멤버별 현황 · 지연 카드를 한 번에 만든다.
//   mongo 조회(라우트)와 집계(여기)를 나눠 두면 기간·회사·보관 규칙이 테스트로 고정된다.
//   ⚠️ 이름 해석은 하지 않는다 — 저장된 표시명만 쓰고, 빈 것은 라우트가 namesByIds 로 덮는다(계정 ID 비노출).

/** 값이 있으면 참 (archivedAt·closedAt 류) — visibleOnCalendar 와 같은 기준. */
const hasVal = (v: unknown): boolean => v !== null && v !== undefined && v !== '';

/** 날짜 → ISO 문자열. 잘못된 값·빈 값은 ''(클라가 '—' 로 표시). */
const toIso = (v: unknown): string => {
	if (!hasVal(v)) return '';
	const d = v instanceof Date ? v : new Date(String(v));
	const t = d.getTime();
	return Number.isFinite(t) ? new Date(t).toISOString() : '';
};

export type PjAdminOverviewOpts = {
	/** 기간 시작 'YYYY-MM-DD'(KST). from·to 둘 다 비면 전체 기간 */
	from?: string;
	/** 기간 끝 'YYYY-MM-DD'(KST) — 이 날짜 **포함** */
	to?: string;
	/** 회사 소속 users._id (core/companyScope.companyUserIds). null·undefined = 회사 필터 없음.
	 *  🔴 **빈 배열은 '그 회사 인원 0명'** 이라 필터가 걸린 채 결과가 비어야 맞다(없다고 전체를 보여 주면 안 된다). */
	companyIds?: Iterable<string> | null;
	/** true 면 보관(archivedAt) 프로젝트도 포함 — 기본 제외 */
	includeArchived?: boolean;
	/** 지연 판정 기준일 'YYYY-MM-DD'(KST). 테스트 고정용 — 없으면 오늘 */
	todayYmd?: string;
};

export type PjAdminKpi = {
	cards: { total: number; done: number; doing: number; todo: number; overdue: number };
	/** 대상 프로젝트를 보관/종료/그 외로 **분할** — active + closed + archived = 대상 프로젝트 수 */
	projects: { active: number; closed: number; archived: number };
	/** done / total (0..1). 총 0 이면 0 */
	completion: number;
};

export type PjAdminProjectRow = {
	pid: string; key: string; name: string; color: string; pmName: string; closed: boolean;
	total: number; done: number; doing: number; todo: number; overdue: number;
	/** 참여 인원(지켜보기 제외, 고유) — 목록 API 의 memberCount 와 같은 기준 */
	members: number;
	/** ISO 문자열 (없으면 '') */
	updatedAt: string;
};

export type PjAdminMemberRow = { userId: string; username: string; name: string; total: number; done: number; overdue: number };

export type PjAdminOverdueRow = {
	pid: string; key: string; no: number; cid: string; title: string;
	/** 마감일 KST 'YYYY-MM-DD' (지연 카드라 항상 값이 있다) */
	dueAt: string;
	/** 담당자 표시명 — 라우트가 namesByIds 로 보강하고 빈 이름은 지운다 */
	ownerNames: string[];
	/** ownerNames 와 같은 순서의 userId — 라우트 이름 보강용(계약 외 부가 필드) */
	ownerIds: string[];
	pjName: string;
};

export type PjAdminOverview = {
	kpi: PjAdminKpi;
	byProject: PjAdminProjectRow[];
	byMember: PjAdminMemberRow[];
	overdueCards: PjAdminOverdueRow[];
	range: { from: string; to: string };
};

export type PjAdminProjectIn = { _id: string; key?: unknown; name?: unknown; color?: unknown; pmId?: unknown; pmName?: unknown; closedAt?: unknown; archivedAt?: unknown; updatedAt?: unknown };
export type PjAdminCardIn = { _id: string; pid?: unknown; no?: unknown; title?: unknown; status?: unknown; dueAt?: unknown; createdAt?: unknown; archivedAt?: unknown; ownerId?: unknown; ownerUsername?: unknown; ownerName?: unknown; owners?: unknown };
export type PjAdminMemberIn = { pid?: unknown; userId?: unknown; role?: unknown };

/** 카드 담당자 — owners[] 우선, 없으면 레거시 ownerId 1명(relationOf 와 같은 폴백). 중복 userId 제거. */
function cardOwners(card: PjAdminCardIn): PjPerson[] {
	const arr = Array.isArray(card.owners) ? card.owners as Array<{ userId?: unknown; username?: unknown; name?: unknown } | null> : [];
	const out: PjPerson[] = [];
	const seen = new Set<string>();
	for (const o of arr) {
		const id = s(o?.userId);
		if (!id || seen.has(id)) continue;
		seen.add(id);
		out.push({ userId: id, username: s(o?.username), name: s(o?.name) });
	}
	if (!out.length && s(card.ownerId)) out.push({ userId: s(card.ownerId), username: s(card.ownerUsername), name: s(card.ownerName) });
	return out;
}

/**
 * 전사 프로젝트 현황 집계 (계약 §1-2).
 *   · 대상 프로젝트 = 보관 제외(includeArchived 면 포함) + 종료 포함. 회사 필터 시 **PM 또는 카드 담당자 중 회사 인원 1명 이상**.
 *   · 대상 카드 = 보관 카드 제외 · 기간(dueAt 의 KST 일자, dueAt 이 없으면 createdAt 폴백)이 [from,to] 안 · 회사 필터 시 owners 에 회사 인원 포함.
 *   · 지연 = 미완료 + dueAt KST 일자 < 오늘 (todayYmd). 상태 버킷 done/doing/todo 는 **합이 total** (도넛용 — 지연은 별도 축).
 *   · byProject 는 카드가 0건인 대상 프로젝트도 포함한다(진행률 0%로 보인다 — 전사 현황이므로 존재 자체가 정보).
 *   · 회사 필터가 걸리면 byMember 는 **그 회사 인원만** 센다(같은 카드의 타사 담당자는 빼서 합계가 회사 범위와 어긋나지 않게).
 * 입력이 비어도 안전한 빈 결과를 돌려준다.
 */
export function aggregateAdminOverview(
	projects: ReadonlyArray<PjAdminProjectIn> | null | undefined,
	cards: ReadonlyArray<PjAdminCardIn> | null | undefined,
	members: ReadonlyArray<PjAdminMemberIn> | null | undefined,
	opts?: PjAdminOverviewOpts,
): PjAdminOverview {
	const from = s(opts?.from).slice(0, 10);
	const to = s(opts?.to).slice(0, 10);
	const includeArchived = opts?.includeArchived === true;
	const today = s(opts?.todayYmd) || kstDateOnly(new Date());
	const companySet = new Set<string>();
	for (const id of opts?.companyIds || []) { const v = s(id); if (v) companySet.add(v); }
	const hasCompany = opts?.companyIds !== null && opts?.companyIds !== undefined;   // 빈 배열도 '필터 걸림'

	// ① 대상 프로젝트 후보 — 보관 필터만 먼저
	const pool = (projects || []).filter((p) => !!p && (includeArchived || !hasVal(p.archivedAt)));
	const poolPids = new Set(pool.map((p) => s(p._id)));

	// ② 후보 프로젝트의 **보관 안 된** 카드 — 담당자·마감일자를 미리 풀어 둔다(이후 필터가 이 배열만 본다)
	type Row = { cid: string; pid: string; status: string; due: string; created: string; overdue: boolean; owners: PjPerson[]; card: PjAdminCardIn };
	const rows: Row[] = [];
	for (const c of cards || []) {
		if (!c) continue;
		const pid = s(c.pid);
		if (!poolPids.has(pid) || hasVal(c.archivedAt)) continue;
		const status = s(c.status) || 'todo';
		const due = kstDateOnly(c.dueAt);
		rows.push({ cid: s(c._id), pid, status, due, created: kstDateOnly(c.createdAt), overdue: status !== 'done' && !!due && due < today, owners: cardOwners(c), card: c });
	}

	// ③ 회사 필터 — PM 이 회사 인원이거나, 그 프로젝트 카드 담당자에 회사 인원이 있으면 대상
	let targets = pool;
	if (hasCompany) {
		const ok = new Set<string>();
		for (const p of pool) if (companySet.has(s(p.pmId))) ok.add(s(p._id));
		for (const r of rows) if (!ok.has(r.pid) && r.owners.some((o) => companySet.has(o.userId))) ok.add(r.pid);
		targets = pool.filter((p) => ok.has(s(p._id)));
	}
	const targetPids = new Set(targets.map((p) => s(p._id)));

	// ④ 대상 카드 — 기간 + 회사
	const inRange = (r: Row): boolean => {
		if (!from && !to) return true;
		const d = r.due || r.created;   // dueAt 이 없을 때만 createdAt 으로 갈음 (계약 §1-2)
		if (!d) return false;
		if (from && d < from) return false;
		if (to && d > to) return false;
		return true;
	};
	const picked = rows.filter((r) => targetPids.has(r.pid) && inRange(r) && (!hasCompany || r.owners.some((o) => companySet.has(o.userId))));

	// ⑤ KPI — 상태 버킷은 done/doing/그 외(todo) 3분할이라 합이 total, 지연은 별도 축
	type Bucket = { total: number; done: number; doing: number; todo: number; overdue: number };
	const newBucket = (): Bucket => ({ total: 0, done: 0, doing: 0, todo: 0, overdue: 0 });
	const bump = (b: Bucket, r: Row): void => {
		b.total++;
		if (r.status === 'done') b.done++;
		else if (r.status === 'doing') b.doing++;
		else b.todo++;
		if (r.overdue) b.overdue++;
	};
	const all = newBucket();
	const byPid = new Map<string, Bucket>();
	for (const r of picked) {
		bump(all, r);
		let b = byPid.get(r.pid);
		if (!b) { b = newBucket(); byPid.set(r.pid, b); }
		bump(b, r);
	}
	let active = 0, closed = 0, archived = 0;
	for (const p of targets) {
		if (hasVal(p.archivedAt)) archived++;
		else if (isClosed(p)) closed++;
		else active++;
	}

	// ⑥ 프로젝트별 인원 — 지켜보기 제외, 고유 userId (목록 API memberCount 와 같은 기준)
	const memByPid = new Map<string, Set<string>>();
	for (const m of members || []) {
		const pid = s(m?.pid), uid = s(m?.userId);
		if (!pid || !uid || !targetPids.has(pid) || s(m?.role) === 'watcher') continue;
		let set = memByPid.get(pid);
		if (!set) { set = new Set<string>(); memByPid.set(pid, set); }
		set.add(uid);
	}

	const byProject: PjAdminProjectRow[] = targets.map((p) => {
		const pid = s(p._id);
		const b = byPid.get(pid) || newBucket();
		return {
			pid, key: s(p.key), name: s(p.name), color: s(p.color), pmName: s(p.pmName), closed: isClosed(p),
			total: b.total, done: b.done, doing: b.doing, todo: b.todo, overdue: b.overdue,
			members: memByPid.get(pid)?.size || 0, updatedAt: toIso(p.updatedAt),
		};
	});
	byProject.sort((a, b) => b.overdue - a.overdue || b.total - a.total || (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0) || a.pid.localeCompare(b.pid));

	// ⑦ 멤버별 — 카드 담당자 펼침. 회사 필터 시 그 회사 인원만
	const memRows = new Map<string, PjAdminMemberRow>();
	for (const r of picked) {
		for (const o of r.owners) {
			if (hasCompany && !companySet.has(o.userId)) continue;
			let e = memRows.get(o.userId);
			if (!e) { e = { userId: o.userId, username: o.username, name: o.name, total: 0, done: 0, overdue: 0 }; memRows.set(o.userId, e); }
			if (!e.name && o.name) e.name = o.name;
			if (!e.username && o.username) e.username = o.username;
			e.total++;
			if (r.status === 'done') e.done++;
			if (r.overdue) e.overdue++;
		}
	}
	const byMember = [...memRows.values()].sort((a, b) => b.total - a.total || b.overdue - a.overdue || a.name.localeCompare(b.name) || a.userId.localeCompare(b.userId)).slice(0, 50);

	// ⑧ 지연 카드 — 마감 이른 순 상위 30
	const pjById = new Map(targets.map((p) => [s(p._id), p]));
	const overdueCards: PjAdminOverdueRow[] = picked
		.filter((r) => r.overdue)
		.sort((a, b) => (a.due < b.due ? -1 : a.due > b.due ? 1 : 0) || a.pid.localeCompare(b.pid) || a.cid.localeCompare(b.cid))
		.slice(0, 30)
		.map((r) => {
			const pj = pjById.get(r.pid);
			const no = Number(r.card.no);
			return {
				pid: r.pid, key: s(pj?.key), no: Number.isInteger(no) && no > 0 ? no : 0, cid: r.cid, title: s(r.card.title),
				dueAt: r.due, ownerNames: r.owners.map((o) => o.name), ownerIds: r.owners.map((o) => o.userId), pjName: s(pj?.name),
			};
		});

	return {
		kpi: {
			cards: { total: all.total, done: all.done, doing: all.doing, todo: all.todo, overdue: all.overdue },
			projects: { active, closed, archived },
			completion: all.total ? all.done / all.total : 0,
		},
		byProject: byProject.slice(0, 200),
		byMember,
		overdueCards,
		range: { from, to },
	};
}

/** 🔕 대화방 알림 게이트 (2026-09-14 사용자 "토픽방 카드·상태변경 알림이 너무 많다 — 설정 토글로").
 *  🔴 **기본은 켜짐** — 필드가 없는 기존 프로젝트가 조용해지면 안 된다(`!roomNotify` 로 쓰면 전부 꺼진다).
 *  명시적으로 false 일 때만 끈다. routes/projects.notifyTopic 이 이 판정을 쓴다. */
export function shouldNotifyRoom(roomNotify: unknown): boolean {
	return roomNotify !== false;
}

/** 📝 [프로젝트 카드에 등록] 팝업의 내용(평문) → 카드 글 HTML (2026-09-28 사용자 "제목 및 내용을 같이 등록").
 *  🔴 사용자 입력이 그대로 카드 본문(HTML)이 되는 경로 — 전부 이스케이프한 뒤 빈 줄=문단, 줄바꿈=<br> 만 만든다(태그 주입 불가).
 *  CRLF 정규화 · 20000자 상한 · 앞뒤 공백 제거. 비면 html='' (카드 글을 만들지 않는다). text = 목록 미리보기용 300자 평문. */
export function plainToCardHtml(input: unknown): { html: string; text: string; plain: string } {
	const plain = String(input ?? '').replace(/\r\n?/g, '\n').slice(0, 20000).trim();
	if (!plain) return { html: '', text: '', plain: '' };
	const esc = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	const html = plain.split(/\n{2,}/).map((p) => `<p>${esc(p).replace(/\n/g, '<br>')}</p>`).join('');
	return { html, text: plain.replace(/\s+/g, ' ').slice(0, 300), plain };
}

/** 🧾 메시지 → 사람이 읽는 평문 (2026-09-28 사용자 "웹훅 내용을 카드 등록 → 제목만 들어감, 내용도 같이 필요").
 *  ① [TPT_NOTICE]{json}(웹훅·알림·업무 카드) → 제목 + 본문 + 링크(링크 문구: 주소). 종전엔 마커 본문을 통째로 비우거나 title 만 썼다.
 *  ② 그 밖의 [TPT_*] 마커(스티커·통화·투표·PJT 공유 등)는 '' — 원시 JSON 을 카드 글에 넣지 않는다.
 *  ③ 일반 글이 비고 attachments(전달·인용 카드)에 글이 있으면 그 제목·본문.
 *  파싱 실패는 '' (원시 JSON 노출 금지). 카드 등록 합치기·AI 요약-제목 입력이 이 함수 하나를 쓴다(클라 PjtShareCard 도 같은 규칙). */
export function messagePlain(msg: unknown, attachments?: unknown): string {
	const s = String(msg ?? '').trim();
	const m = /^\[(TPT_[A-Z_]+)\]([\s\S]*)$/.exec(s);
	if (m) {
		if (m[1] !== 'TPT_NOTICE') return '';
		try {
			const j = JSON.parse(m[2]) as Record<string, unknown>;
			const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
			const title = str(j.title);
			const text = str(j.text);
			const link = str(j.pcLink) || str(j.mobileLink);
			const linkLine = link ? (str(j.linkText) ? `${str(j.linkText)}: ${link}` : link) : '';
			// 본문 첫 줄이 제목과 같으면(구 카드 폴백 형식) 제목을 한 번만
			const body = title && text.split('\n')[0].trim() === title ? text.split('\n').slice(1).join('\n').trim() : text;
			return [title, body, linkLine].filter(Boolean).join('\n');
		} catch { return ''; }
	}
	if (s) return s;
	if (Array.isArray(attachments)) {
		return (attachments as Array<Record<string, unknown>>)
			.map((a) => [a?.title, a?.text].map((v) => (typeof v === 'string' ? v.trim() : '')).filter(Boolean).join('\n'))
			.filter(Boolean).join('\n\n');
	}
	return '';
}

/** 🌐 번역 맵에서 한 필드만 — { vi: { title: '…' }, … } (문자열인 것만). 없으면 undefined. /my-summary 가 홈 위젯용으로 쓴다 (2026-09-28) */
export function txPick(tx: unknown, field: 'name' | 'title'): Record<string, Record<string, string>> | undefined {
	if (!tx || typeof tx !== 'object') return undefined;
	const out: Record<string, Record<string, string>> = {};
	for (const [lang, v] of Object.entries(tx as Record<string, unknown>)) {
		const s = v && typeof v === 'object' ? (v as Record<string, unknown>)[field] : undefined;
		if (typeof s === 'string' && s.trim()) out[lang] = { [field]: s };
	}
	return Object.keys(out).length ? out : undefined;
}

/**
 * 📤 대화 내보내기 API(/api/tpt/export/user-messages)에 싣는 프로젝트 업무 카드 한 줄 (2026-10-06 형 원격
 *   "대화 가져가는 API 에 그 사용자에 맞는 프로젝트 업무 내역도 보내 줬으면").
 *   relation = 이 사용자가 담당(owner)인지 참조(watcher)인지 — 둘 다면 owner. 날짜는 ISO(없으면 ''), 키는 항상 실린다.
 */
export function exportCardItem(
	card: { _id: string; pid?: string; sid?: string; no?: number; title?: string; status?: string; priority?: string; startAt?: unknown; dueAt?: unknown; createdAt?: unknown; updatedAt?: unknown; bodyText?: string; owners?: Array<{ userId?: string; name?: string; username?: string }>; ownerId?: string; watchers?: Array<{ userId?: string }> },
	project: { name?: string; key?: string; closedAt?: unknown } | undefined,
	stageName: string,
	userId: string,
): Record<string, unknown> {
	const iso = (v: unknown) => { if (!v) return ''; const d = new Date(v as string); return Number.isFinite(d.getTime()) ? d.toISOString() : ''; };
	const isOwner = card.ownerId === userId || (card.owners || []).some((o) => o?.userId === userId);
	const key = String(project?.key || '');
	return {
		projectId: String(card.pid || ''),
		projectName: String(project?.name || ''),
		projectKey: key,
		projectClosed: isClosed(project),
		stage: stageName,
		cardId: String(card._id),
		cardNo: key && card.no ? `${key}-${card.no}` : '',
		title: String(card.title || ''),
		status: String(card.status || 'todo'),
		priority: String(card.priority || 'normal'),
		relation: isOwner ? 'owner' : 'watcher',
		owners: (card.owners || []).map((o) => String(o?.name || o?.username || '')).filter(Boolean),
		startAt: iso(card.startAt),
		dueAt: iso(card.dueAt),
		createdAt: iso(card.createdAt),
		updatedAt: iso(card.updatedAt),
		summary: String(card.bodyText || '').slice(0, 500),
	};
}

/** 📤 내보내기 작업내역의 action → 한국어 라벨 (외부 연동 운영 문서용 — 한국어 고정). 없는 action 은 코드 그대로 */
export const EXPORT_ACTIVITY_LABEL: Readonly<Record<string, string>> = {
	'card.add': '업무 등록', 'card.register': '대화에서 업무 등록', 'card.duplicate': '업무 복제', 'card.update': '업무 수정',
	'card.move': '단계 이동', 'card.remove': '업무 삭제', 'card.share': '대화방 공유', 'card.unshare': '대화방 공유 해제',
	'card.comment': '댓글', 'post.save': '글 작성', 'post.append': '글 추가', 'post.restore': '글 되돌리기', 'version.add': '산출물 업로드', 'version.restore': '산출물 복원', 'version.exclude': '산출물 제외', 'version.include': '산출물 되살리기',
	'stage.add': '단계 추가', 'stage.rename': '단계 이름 변경', 'stage.remove': '단계 삭제', 'stage.copycards': '업무 복사',
	'project.create': '프로젝트 생성', 'project.update': '프로젝트 수정', 'project.langs': '번역 언어 변경',
	'member.add': '참여자 추가', 'member.remove': '참여자 제외', 'member.role': '역할 변경', 'room.link': '토픽 연결', 'room.unlink': '토픽 연결 해제',
};
/** 순서 바꾸기만 한 기록은 업무 내역이 아니다 — 내보내기에서 뺀다 */
export const EXPORT_ACTIVITY_SKIP: readonly string[] = ['card.reorder', 'stage.reorder'];
const CHANGE_FIELD_LABEL: Record<PjChangeField, string> = { title: '제목', status: '상태', priority: '우선순위', startAt: '시작일', dueAt: '마감일', owners: '담당', watchers: '참조', checklist: '체크리스트', sid: '단계' };

/**
 * 📤 대화 내보내기 API 의 프로젝트 작업내역 한 줄 (2026-10-07 형 원격 "토픽의 PJT 외에도 PJT 기능의 내역 — 내가 작성한 업무내역").
 *   원천 = tpt_project_audit(byId=본인) 한 행, 또는 본인이 쓴 카드 댓글(action 'card.comment', detail.msg).
 *   detail 은 사람이 읽는 한 줄 — 수정은 '상태: 진행 → 완료 / 마감일: … → …', 산출물은 'v3 결과.pdf', 댓글은 본문(최대 1000자).
 *   카드가 지워졌으면 audit 에 남은 제목(detail.title)을 쓴다. 키는 항상 실린다(없으면 '').
 */
export function exportActivityItem(
	row: { at?: unknown; action?: string; detail?: Record<string, unknown> },
	project: { _id?: string; name?: string; key?: string } | undefined,
	card: { _id?: string; no?: number; title?: string } | undefined,
	stageName: string,
): Record<string, unknown> {
	const d = row.detail || {};
	const action = s(row.action);
	const at = row.at ? new Date(row.at as string) : null;
	const key = s(project?.key);
	let detail = '';
	if (action === 'card.comment') detail = s(d.msg).slice(0, 1000);
	else if (Array.isArray(d.changes) && d.changes.length) {
		detail = (d.changes as Array<{ field?: PjChangeField; from?: unknown; to?: unknown }>)
			.map((c) => `${(c.field && CHANGE_FIELD_LABEL[c.field]) || s(c.field)}: ${s(c.from) || '—'} → ${s(c.to) || '—'}`).join(' / ');
	} else if ((action === 'version.add' || action === 'version.restore') && d.round) {
		// 📎 여러 파일 이후(2026-10-07) — '파일명 (n회차 · r개정)'. 교체·되돌리기는 꼬리표
		const tag = action === 'version.restore' ? ' · 되돌리기' : d.replace ? ' · 교체' : '';
		detail = `${s(d.name)} (${s(d.round)}회차 · r${s(d.rev)}${tag})`;
	} else if (action === 'version.add') detail = `v${s(d.ver)} ${s(d.name)}`.trim();
	else if (action === 'version.restore') detail = `v${s(d.from)} → v${s(d.to)}`;
	else if (action === 'version.exclude' || action === 'version.include') detail = s(d.name);
	else if (action.startsWith('member.')) detail = [s(d.name || d.username), s(d.role)].filter(Boolean).join(' · ');
	else if (action === 'stage.copycards') detail = `${s(d.from)} ${s(d.n)}건`;
	else if (action.startsWith('stage.') || action === 'project.create') detail = s(d.name);
	return {
		at: at && Number.isFinite(at.getTime()) ? at.toISOString() : '',
		action,
		actionLabel: EXPORT_ACTIVITY_LABEL[action] || action,
		projectId: s(project?._id),
		projectName: s(project?.name),
		projectKey: key,
		cardId: s(card?._id || d.cid),
		cardNo: key && card?.no ? `${key}-${card.no}` : '',
		cardTitle: s(card?.title || d.title),
		stage: stageName,
		detail,
	};
}
